import { createFileRoute, redirect, useParams } from "@tanstack/react-router";

import type { Audience } from "@/features/Audiences/types";
import type { QueryClient } from "@tanstack/react-query";

import { getAudienceQueryOptions } from "@/api/audiences";
import { EditAudience } from "@/features/Audiences/EditAudience";
import { returnToAudienceSchema } from "@/features/Audiences/return";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

interface AudienceLoaderData {
  audience: Audience;
}

export const Route = createFileRoute(
  "/_auth/people/$siteId/audiences/$audienceId/edit",
)({
  validateSearch: (raw) => returnToAudienceSchema.parse(raw),
  component: EditAudiencePage,
  loader: async ({ params, context }): Promise<AudienceLoaderData> => {
    const { queryClient } = context as { queryClient: QueryClient };
    const { siteId, audienceId } = params;

    const audience = await queryClient.ensureQueryData(
      getAudienceQueryOptions(siteId, audienceId),
    );

    if (!audience) {
      enqueueErrorSnackbar(
        new Error("Audience not found"),
        "Failed to get audience.",
      );
      throw redirect({
        to: "/people/$siteId/audiences",
        params: {
          siteId: siteId,
        },
      });
    }

    return { audience };
  },
});

function EditAudiencePage() {
  const { audience } = Route.useLoaderData();
  const { siteId } = useParams({
    from: "/_auth/people/$siteId/audiences/$audienceId/edit",
  });
  return <EditAudience siteId={siteId} audience={audience} />;
}
